/**
 * Frontend-only addition to the shared Sentry/GlitchTip scrubber.
 *
 * A group share link carries its bearer token IN THE PATH (`/o/<token>`,
 * `/api/public/group-offer/<token>/…`), where no query-param rule sees it
 * (security review L11). The token is 43 base64url characters; whoever reads it
 * in an error report can open the agency's offer and accept it.
 *
 * Only the frontend ever serves or requests those paths, so the rule lives here
 * rather than in the canonical scrubber (`taskyinn-backend/shared/sentry/`),
 * which is vendored byte-for-byte into every backend service.
 */
import { REDACTED, scrubSentryEvent } from './sentry-scrub'

const SHARE_PATH_TOKEN = /(\/(?:o|group-offer)\/)[A-Za-z0-9_-]{43}(?![A-Za-z0-9_-])/g

/** Deepest nesting walked; a Sentry event is a shallow tree, this only bounds a cycle. */
const MAX_DEPTH = 12

/** Replace a share-link token in a path with the redaction marker. */
export function redactPathTokens(text: string): string {
  return text.indexOf('/o/') === -1 && text.indexOf('/group-offer/') === -1 ? text : text.replace(SHARE_PATH_TOKEN, `$1${REDACTED}`)
}

/** Redact path tokens in every string of a value, in place for objects and arrays. */
function redactDeep(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return redactPathTokens(value)
  if (!value || typeof value !== 'object' || depth > MAX_DEPTH) return value
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = redactDeep(value[i], depth + 1)
    return value
  }
  const record = value as Record<string, unknown>
  for (const key of Object.keys(record)) record[key] = redactDeep(record[key], depth + 1)
  return value
}

/**
 * The frontend's `beforeSend`: the shared scrubber, then share-link tokens
 * removed from every string of the event (request, transaction, spans,
 * breadcrumbs, message, exception, extra, tags, contexts).
 */
export function scrubSentryEventAndLinkTokens<T>(event: T): T {
  return redactDeep(scrubSentryEvent(event), 0) as T
}
